import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ContentChild,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Observable } from 'rxjs/Observable';
import { cloneDeep, get, has, isEmpty, set, unset } from 'lodash';
import { filter, map, switchMap, take, takeUntil } from 'rxjs/operators';
import { isObservable, merge, Subject } from 'rxjs';
import { of } from 'rxjs/observable/of';

import { DynamicFormModalPayload } from '@ispm/dynamic-form/classes/dynamic-form-modal-payload.class';
import { ITEMS_TYPES } from '../../constants/ItemsTypes';
import { IdynamicFormItem } from '../../interfaces/IdynamicFormItem';
import { InterpolateService } from '@ispm/shared/services/interpolate.service';
import { IoptionElemet } from '@ispm/shared/interfaces/IoptionElemet';
import { MOUSE_EVENT_TYPES } from '@ispm/dynamic-form/constants/mouseEvents.enum';
import { SearchService } from '../../services/search.service';
import { VALUE_TYPES } from '@ispm/dynamic-form/constants/value-type.enum';
import { getRandomInt } from '@ispm/dynamic-form/utilities/item.util';
import { searchConstants } from '@ispm/dynamic-form/services/search.constants';
import { tooltipPositions } from '@ispm/shared/directives/tooltips/tooltip.directive';
import { AutoComplete } from 'primeng/autocomplete';
import { AgregationService } from '@ispm-client/modules/aggregator/services';
import { EditableParameterComponent } from '@ispm/shared/components/editable-parameter/editable-parameter.component';
import { TranslationPipe } from '@ispm/shared/pipes/string-translate.pipe';
import { FormulaService } from '@ispm/shared/services/formula.service';
import { StringUtils } from '@ispm/shared/utils/String.utils';
import _ from 'lodash';
import { DomHandler } from 'primeng/dom';

@Component({
  selector: 'gen-form-item',
  templateUrl: './form-item.component.html',
  styleUrls: ['./form-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
  providers: [TranslationPipe]
})
export class FormItemComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {
  @ViewChild('multiselect') multiselect;

  @Input()
  set defaultList(list: Array<any>) {
    this._defaultList = list;
  }

  @Input()
  set item(_item: IdynamicFormItem) {
    const item = _item;

    this.isClickable = FormItemComponent.clickableTypes.includes((<string> item.type).toUpperCase());
    this.isIndependent = FormItemComponent.selfHandledTypes.includes((<string> item.type).toUpperCase());

    // due to changes in common css files previous defined margin is not relevant now, so if margin is needed
    // dynamic form item should have 'ignoreMargin' = false attribute
    this.ignoreMargin = get(_item.attributes, 'ignoreMargin', true) as boolean;
    this.noEmptyEmit = get(_item.attributes, '[noEmptyEmit]', false) as boolean;
    // need to move to configuration
    this.forceDeepBlur = get(_item.attributes, '[forceDeepBlur]', false) as boolean;
    this.readOnlyView = get(_item.attributes, '[readOnlyView]', false) as boolean;
    this.isTriggerBlurItem = get(_item.attributes, '[triggerBlurItem]', true) as boolean;

    const widgetComponent = get(_item, 'options.component');
    this.updateOn = _item.updateOn ? _item.updateOn : this.getUpdateOnConfig(_item, widgetComponent);

    if (this.getItemEnum(_item.type) === ITEMS_TYPES.WIDGET) {
      if (!get(_item.attributes, '[hideInvalidTooltip]', true) ) {
        this.hideInvalidTooltip = false;
      }
      else { this.hideInvalidTooltip = true; }
    }
    else if (get(_item.attributes, '[hideInvalidTooltip]', false)) {
      this.hideInvalidTooltip = true;
    }

    if (item.options && item.options.values && !item.options.resource) {
      this.options$ = Observable.of(item.options.values);
      this.options = item.options.values;
      this.fieldsToDisplay = item.fieldsToDisplay || [];

      if (typeof this.options[0] === 'string') {
        this.transformToTemplate = { display: null, code: null, shortName: null, code1: null, cssClass: null};
      }
    }

    item.attributes = item.attributes ?  cloneDeep(item.attributes) : { };

    if (item.attributes &&
      item.attributes.editableItemEmptyValueText) {

      this.editableItemEmptyValueText = item.attributes.editableItemEmptyValueText;
    }

    this.formalAttributes(item);
    this.labelTooltipText$ = (has(item, 'tooltip') && !isEmpty(item.tooltip)) ? of(item.tooltip) : of(item.label);
    this.valueTooltipText$ = (has(item, 'tooltip') && !isEmpty(item.tooltip) && ((has(item, 'label') && isEmpty(item.label.trim())) || !has(item, 'label')) ? of(item.tooltip) : of(null));

    this._item = item;

    if (this.formInstance) {
      this.setValueListener();
    }
  }

  get item() {
    return this._item;
  }

  set alignment(val) {
    this._alignment = val;
    this.updateTouched(null, true);
  }

  get alignment () {
    return this._alignment;
  }

  @Input()
  set editMode(editMode: boolean) {
    this._editMode = editMode;

    if (!this.mode) {
      this.customMode = editMode ? 'text' : 'form';
    }

   this.setViewModeCheck();
  }

  get editMode() {
    return this._editMode;
  }

  @Input()
  set formInstance(formInstance: FormGroup) {
    this._formInstance = formInstance;
    if (this.item) {
        this.setValueListener();
    }
  }

  get formInstance(): FormGroup {
    return this._formInstance;
  }

  @Input()
  set formBaseWidgetPayload(formBaseWidgetPayload: DynamicFormModalPayload) {
    this._formBaseWidgetPayload = formBaseWidgetPayload.disabled === this.disabled // reduce the amount of allocations
      ? formBaseWidgetPayload
      : Object.assign({ }, formBaseWidgetPayload, { disabled: this.disabled});
  }

  get formBaseWidgetPayload () {
    return this._formBaseWidgetPayload;
  }

  @Input()
  set openAfterEdit(stayOn: string) {
    this._openAfterEditTypes = stayOn.split(',').map(v => this.getItemEnum(v));
    this._openAfterEdit = stayOn;
  }

  get openAfterEdit() {
    return this._openAfterEdit;
  }

  /**
   * In case we load the dynamic form inside of a popup we would like the dropdown and calender options
   * to overflow to outside of the popup incase of overflow, default is false
   * @type {boolean}
   */
  @Input()
  set popupMode(status: any) {
    this._appendTo = !!status ? 'body' : null;
  }

  get popupMode(): any {
    return this._appendTo;
  }

  set showMenu(value: any) {
    this.menuData = value ? { value: value.value, x: value.event.x, y: value.event.y} : null;
    this.contextMenuPayload = Object.assign(this.contextMenuPayload, {
      payload: this.menuData,
      type: this.item.contextMenuType,
      active: this.showMenu
    });
  }

  get showMenu() {
    return this.menuData !== null;
  }

  set itemValue(update) {
    this.updateForm(update);
  }

  get itemValue() {
    return this.getValue(this.item.name);
  }

  get itemValueClass() {
    let val = this.itemValue;

    if (val) {
      if (Array.isArray(val)) {
        val = val.join(',');
      }
      else if (typeof val === 'object') {
        val = val[this.transformToTemplate.code];
      }

      if (val) {
        val = 'visual_display ' + val.toString().toUpperCase().split(' ').join('_');
      }
    }
    return val;
  }

  constructor(
    private _search: SearchService,
    private _aggregationService: AgregationService,
    private element: ElementRef,
    private stringTranslate: TranslationPipe
  ) { }

  static readonly clickableTypes: string[] = [ITEMS_TYPES.CHECKBOX, ITEMS_TYPES.RADIO, ITEMS_TYPES.SELECT, ITEMS_TYPES.MULTISELECT_WITH_MANUAL]; // ITEMS_TYPES.SELECT was added in order to handle GEN-88498. It seems that ALL dynamic forms select type components do not fire updateForm() event upon changes when "[forceFormMode]": true
  static readonly selfHandledTypes: string[] = [ITEMS_TYPES.WIDGET];
  static inputsRegex = /\[.*\]/i;
  static formModeFields: string[] = [
    ITEMS_TYPES.UPLOAD,
    ITEMS_TYPES.CHECKBOX,
    ITEMS_TYPES.RADIO,
    ITEMS_TYPES.CONTAINER,
    ITEMS_TYPES.WIDGET,
    ITEMS_TYPES.DERIVED
  ];
  static readonly DEFAULT_UNCLICKABLE_ON_UPDATE_COMPONENTS = [
    'DYNAMICFORMTABLECOMPONENT', 'DYNAMICFORMTABLE', 'PRIORITYTABLECOMPONENT', 'PRIORITYTABLE'
  ];

  public updateOn: MOUSE_EVENT_TYPES = MOUSE_EVENT_TYPES.click;
  public tooltipPosition = tooltipPositions.BOTTOM;
  public tooltipText$: Observable<string>;
  public labelTooltipText$: Observable<string>;
  public valueTooltipText$: Observable<string>;
  public tooltipShow: boolean;
  public _alignment: string = 'true';

  private noEmptyEmit = false;
  private lastTouchedValue: any;

  @ViewChild(EditableParameterComponent) private editableParameter: EditableParameterComponent;
  @Output() itemTouched: EventEmitter<{ name: string, blured: boolean }> = new EventEmitter();
  @Output() itemBlured: EventEmitter<void> = new EventEmitter();
  @Output() linkClick: EventEmitter<string> = new EventEmitter<string>();
  @Output() onAddControl: EventEmitter<IdynamicFormItem> = new EventEmitter();
  @Output() forceChangeEmitter: EventEmitter<boolean> = new EventEmitter();
  @Output() asyncLoader: EventEmitter<boolean> = new EventEmitter();
  @Output() public submitUpdateEmitter: EventEmitter<boolean> = new EventEmitter();
  @Input() formId: string;
  @Input() textAreaResizable: boolean;
  @Input() textMinHeight: string;
  @Input() textMaxHeight: string;
  @Input() additionalSearchValues: any;
  @Input() mode: 'form' | 'text';
  @Input() itemIndex: string;
  _defaultList: Array<any>;
  private _item: IdynamicFormItem;

  public shouldStayOpen: boolean;

  @Input() disabledOptions: string[];

  @Input()
  public isIE: boolean;

  @Input()
  public stayInFormMode: boolean; // Orel - todo: check if can be removed

  public hideInvalidTooltip: boolean;
  public isIndependent: boolean;
  public editableInitMode: boolean;
  public ignoreMargin: boolean = true;
  private _formInstance: FormGroup;

  @Input() submitted: FormGroup;
  @Input() disabled: boolean = false;
  @Input() requiredMessage?: string = 'Required information missing';

  private _formBaseWidgetPayload: DynamicFormModalPayload;

  public formMode: boolean = false;
  public customMode: 'form' | 'text';
  public fullFormData$: Observable<any>;
  public isFieldEditable: boolean;
  public isClickable: boolean;
  public forceDeepBlur: boolean;
  public readOnlyView: boolean;
  public randomId: string = ' ';
  private lastQuery$: Observable<any> = Observable.of({ });
  private _openAfterEdit: string = 'multiselect';
  private _openAfterEditTypes: Array<string> = [this.getItemEnum('multiselect')];
  public editableItemEmptyValueText: string;
  public disableTillFetched: boolean = false;
  public isTriggerBlurItem: boolean;

  public fieldsToDisplay: Array<string | {
    path?: string,
    source?: string,
    display: string
  }> = [];
  public focusedElement: any;
  public localSearch: boolean = false;
  public options$: Observable<IoptionElemet[] | any[]> = Observable.of([]);
  public options: IoptionElemet[] | any[] = [];
  public forceSelection: boolean = false;
  public skipOnBlur: boolean = false;
  public transformToTemplate: { display: string, code: string, shortName: string, code1: string, cssClass?: string } =
    { display: 'display', code: 'code', shortName: 'shortName', code1: undefined, cssClass: 'cssClass'};
  public automationClass: string;
  public forceItemFormMode: boolean = false;
  public multiple: boolean;
  private _appendTo: string = null;

  public primeNgTransformTemplate: { };
  public value$: Observable<any>;
  private menuData = null;
  private keyIsArray: boolean;
  private dateTime: Date = new Date();
  private _editMode: boolean;
  private _itemTempValue: any;
  private _destroyer: Subject<void> = new Subject();

  @ContentChild('contextMenu', { static: false}) contextTemplate;
  @ContentChild('containerContent', { static: false}) containerContent;

  public contextMenuPayload = {
    payload: null,
    getter: () => this.itemValue,
    setter: (update) => {
      let item = [...this.itemValue];

      if (this.contextMenuPayload.payload.value !== null) {
        item[this.contextMenuPayload.payload.value] = Object.assign({ }, item[this.contextMenuPayload.payload.value], update);
      } else {
        item = Object.assign({ }, item, update);

      }

      this.itemValue = item;
      this.itemTouched.emit({ name: this.item.name, blured: true});

      this.formInstance.controls[this.item.name].patchValue(item);
    }
  };

  public static OverrideOnInputBlur() {
    // workaround for primeng bug until upgrade to primeng11 https://github.com/primefaces/primeng/issues/8740
    AutoComplete.prototype.onInputBlur = function( this: AutoComplete, event: KeyboardEvent ) {
      try {
        if ( this.highlightOption ) {
          this.selectItem( this.highlightOption, false );
          this.cd.detectChanges();
        }
      } catch ( e ) {
      }

      this.focus = false;
      this.onModelTouched();
      this.onBlur.emit( event );
    };
  }

  ngOnInit() {
    if (this.item.options && this.item.options.values) {
      this.options$ = Observable.of(this.item.options.values);
      this.options = this.item.options.values;
      this.fieldsToDisplay = this.item.fieldsToDisplay || [];

      if (typeof this.options[0] === 'string' || this.options[0] == null) {
        this.transformToTemplate = { display: null, code: null, shortName: null, code1: null, cssClass: null};
      }

      this.autoSelectOption();
    } else if (this.item.options && this.item.options.resource) {
      const fields = this.item.options.resource.split('|');
      this.keyIsArray = fields.length > 1 && searchConstants.ARRAY_REGEX.test(fields[1]);
      this.setResourceOptions();

      // pre-fetching data
      if (this.needPrefetchOrNot(this.item)) {
        this.search({ query: ''});
        this.autoSelectOption();
      }
    }

    this.item.valueProperties = this.transformToTemplate;
    this.item.attributes = this.item.attributes ?  cloneDeep(this.item.attributes) : { };
    this.setViewMode();
    const itemLabel =  this.item.label ?  this.item.label.split(' ').join('_').toUpperCase() : 'No_Label';
    this.automationClass = `${itemLabel} ${this.item.name} Auto_Input`;
    this.formalAttributes(this.item);
    this.applyItemChecks();
    this.setValueListener();
    this.randomId = getRandomInt();
  }

  autoSelectOption() {
    if (this.item.options && this.item.options.autoSelect === true) {
      this.options$.pipe(take(1)).subscribe(x => {
        this.updateTouched(null, true);
        if (this.itemValue) {
          this.updateForm(x.find(option => option.code === this.itemValue) || x[0]);
        } else {
          this.updateForm(x[0]);
        }
      });
    }
  }

  setViewMode() {
      this.forceItemFormMode = get(this.item, 'attributes["[forceFormMode]"]', false)
      || get(this.item, 'attributes["[binary]"]', false);
      this.isFieldEditable = !(this.item.attributes && this.item.attributes.previewOnly)
                                && (!this.editMode || (this.editMode && this.item.editable !== false));
      const mode = this.mode || this.customMode;
      const editableInitModeFallback =  mode ? mode === 'form' : this.editMode;
      const previewOnly = get(this.item, 'attributes.previewOnly', false);
      const editableInitMode = get(this.item, 'attributes.editableInitMode', editableInitModeFallback);
      this.editableInitMode = this.isIndependent || (!previewOnly && editableInitMode);

      if (this.isFieldEditable) {
          this.formMode = this.forceItemFormMode || FormItemComponent.formModeFields.includes(this.item.type.toUpperCase());
          if (!this.formMode && mode) {
              this.formMode = mode === 'form';
          }
      } else if (!this.editMode) {
          this.formMode = FormItemComponent.formModeFields.includes(this.item.type.toUpperCase());
      } else {
          this.formMode = false;
      }
  }

  setViewModeCheck() {
      if (this.item) {
          this.setViewMode();
      }
  }

  applyItemChecks() {
    this.shouldStayOpen = this._openAfterEditTypes.includes(this.getItemEnum(this.item.type));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.item && !changes.item.firstChange
      && changes.item.currentValue.options && changes.item.previousValue.options
      && changes.item.currentValue.options.resource !== changes.item.previousValue.options.resource) {
      this.setResourceOptions();
    }
  }

  /*  GEN-121729
    three functions (splitPattern, getKey, getSuggestionValue) introduced to
    dynamically change display pattern for the entities that have an extra code,
    please see all remarks marked GEN-121729
  */
  private splitPattern(): string[] {
    let pattern: string[] = [];

    if (this.item.displayPattern && (typeof this.item.displayPattern === 'string')) {
      pattern = (<string> this.item.displayPattern).split('||')
        .map(el => el.replace(/[\W_]+/g, ''));
    }

    return pattern;
  }

  getKey(key): string {
    let retKey: string = key;

    if (this.itemValue) {
      const keys: string[] = this.splitPattern();

      if (keys.length > 0) {
        if (this.itemValue.hasOwnProperty(keys[0])) {
          retKey = keys[0]; // !!! see comment to the getSuggestionValue() function !!!
        } else if (key && !this.itemValue.hasOwnProperty(key)) {
          const tmpKey = keys.find(itm => itm !== key);
          retKey = tmpKey ? tmpKey : key;
        }
      }
    }

    this.transformToTemplate && (this.transformToTemplate.display = retKey);

    return retKey;
  }

  getSuggestionValue(value) {
    // Important: if displayPattern consist of two (like $(externalCode || code)
    // externalCode MUST be the first!!
    let retVal;
    const keys = this.splitPattern();
    const key = keys.length > 0 ? keys[0] : this.transformToTemplate?.display;

    if (value && key) {
      if (value[key]) {
        return value[key];
      } else {
        const tmpKey = keys.find(itm => itm !== key);
        retVal = tmpKey ? value[tmpKey] : value[key];
      }
    }

    return retVal ? retVal : value;
  }

  setResourceOptions() {
    this.localSearch = !InterpolateService.hasInterpolationString(this.item.options.resource);
    this.transformToTemplate = this._search.getTransformObject(this.item.options.resource);

    if (this.transformToTemplate) {
      this.primeNgTransformTemplate = { };

      for (const key in this.transformToTemplate) {
        this.primeNgTransformTemplate[key] = this.transformToTemplate[key];
      }
    } else {
      this.primeNgTransformTemplate = null;
    }

    this.fieldsToDisplay = this.item.fieldsToDisplay || [];
  }

  shouldShowTooltip(): boolean {
    if (this.item.isRootProperty) {
      return false;
    }
    else if (this.getControl().invalid && (this.isTooltipNotNone(this.item.tooltip) || this.item.attributes.showCustomTooltip)) {
      return !!(this.submitted
        || this.editMode
        || (this.getControl().dirty && this.getControl().value && this.getControl().value !== ''));
    }
    else {
      return false;
    }
  }

  getItemEnum(itemType) {
    return ITEMS_TYPES[itemType.toUpperCase()] || null;
  }

  isTooltipNotNone(tooltip) {
    return  tooltip !== 'none';
  }

  addControl(formConfig: IdynamicFormItem) {
    this.onAddControl.emit(formConfig);
  }

  onFocus($event) {
    this.focusedElement = $event.target.closest('*[deep-blur]');
    this.updateTouched($event, false);
  }

  onBlur($event, resetOnEmpty: boolean = true) {
    if (this.skipOnBlur){
      return;
    }

    if (!this.forceSelection && resetOnEmpty && $event.target.value.trim() === '') {
      this.formInstance.controls[this.item.name].reset();
      this.formInstance.controls[this.item.name].markAsDirty();
    }

    if (resetOnEmpty && $event.target.value.trim() === '') {
      this.formInstance.controls[this.item.name].reset();
      this.formInstance.controls[this.item.name].markAsDirty();
    } else {
      this.formInstance.controls[this.item.name].patchValue(this.getFormalizeStoreObject());
    }

    if (this.forceDeepBlur) {
      this.element.nativeElement.dispatchEvent(
        new CustomEvent('deepBlur', { bubbles: true})
      );
      this.editableParameter.onInputBlur(this.editableParameter);
    }
  }

  onClear($event) {
    /* on clearing of auto complete this.suggestions inside autocomplete.js getting nullified
       because of which down arrow keyboard press doesnt show suggestions */
    if ($event.composed) {
      this.search({ originalEvent: $event, query: ''});
    }
     this.formInstance.controls[this.item.name].reset();
     this.formInstance.controls[this.item.name].markAsDirty();
  }

  toggleMenu($event: { event: Event, value: any }) {
    if (!this.item.contextMenuType) {
      return;
    }

    $event.event.stopPropagation();
    $event.event.preventDefault();

     // Not assigning toggle menu on right click based on disableToggleMenuByField attribute
     const currentValue = this.getValue(this.item.name);
     const valuetag: boolean = currentValue[$event.value];
     if (valuetag && this.item.hasOwnProperty('disableToggleMenuByField')) {
       if (valuetag[this.item['disableToggleMenuByField']]) {
         return;
       }
     }
    this.showMenu = $event;
  }

  getFormalizeStoreObject() {
    const currentValue = this.getValue(this.item.name);

    if (currentValue && typeof currentValue === 'string'
      && (this.item.options && (this.item.options.resource || this.item.options.values))) {
      if (this.transformToTemplate && this.transformToTemplate.code && this.transformToTemplate.display) {
        const storeObject = { };
        storeObject[this.transformToTemplate.code] = currentValue;
        storeObject[this.transformToTemplate.display] = currentValue;
        storeObject['system'] = 'MANUAL';
        return storeObject;
      }
      else {
        return currentValue;
      }
    } else {
      return this.getNormalizeValue(currentValue);
    }
  }

  /**
   * in case true sent - any change will propogate the parent listener
   * useful for widgets that use rootProperty
   *
   * @param forceChange
   */
  setForceChange(forceChange: boolean) {
    this.updateTouched(null, false);
    this.forceChangeEmitter.emit(forceChange);
  }

  private setDisabledTillFetched(value) {
    this.disableTillFetched = value;
  }

  search(event) {
    if (this.item.options.values) {
      let results = this.item.options.values;

      if (event.query) {
        results = this.localFilter(results, event.query);
      }

      this.options$ = Observable.of([...results]);
    } else if (this.item.options.resource) {
      this.setDisabledTillFetched(true);
      let resource;
      this.options$ = this.lastQuery$
        .pipe(takeUntil(this._destroyer.asObservable()),
          switchMap(async () => {
            if (this.item['interpolateFromStore']) {
              resource = await this._aggregationService.interPolateWithStore(this.item.options.resource);
              if (resource.indexOf('' + { }) >= 0) {
                resource = await this._aggregationService.interPolateWithSessionStorage(this.item.options.resource);
              }
              this.item.options.resource = resource;
            }
            // Passing parameters from all form fields is supported. This is the json format: $(searchTerm.form.gene.display)
            return this._search.query(
              this.item.options.resource,
              event.query,
              { form: this.formInstance.value, additionalData: this.additionalSearchValues },
              this.item.options.encodeQueryParam,
              this.item.attributes,
              get(this.item, 'options.useOriginalOrgId', false)
            ).pipe(map ((res) => {
              if (this.item.options.resourceArrayKey && !isEmpty(res) ) {
                return get(res[0], this.item.options.resourceArrayKey, []);
              }
              return res;
            }));
          }),
          switchMap((values: any ) => {
            if (isObservable(values)) {
              return values.pipe(
                filter(val => !!val));
            }

            else {
              return values;
            }
          }),
          map(elements => cloneDeep(elements)),
          map((elements: any) => {
            if (this.keyIsArray) {
              elements = elements.map(element => this._search.formalize(this.item.options.resource, element));
            }
            return elements;
          }),
          map((elements: any) => {
            if (this.item.options.excludeValue) {
              elements = elements.filter(element => element.id !== this.item.options.excludeValue);
            }
            this.setDisabledTillFetched(false);
            return elements;
          }),
          map((elements: any) => {
            if (!isEmpty(this.item.options.conditionalFilter)
              && Array.isArray(this.item.options.conditionalFilter.conditions)
              && !isEmpty(this.item.options.conditionalFilter.conditions)
              && !isEmpty(this.item.options.conditionalFilter.action)) {
              elements = elements.filter(element => {
                let returnValue = false;
                this.item.options.conditionalFilter.conditions.forEach(item => {
                  if (this.item.options.conditionalFilter.action.includes('EXCLUDE')) {
                    returnValue = !FormulaService.evaluateFormula(get(item, 'formula'), element);
                  }
                  else if (this.item.options.conditionalFilter.action.includes('INCLUDE')) {
                    returnValue = FormulaService.evaluateFormula(get(item, 'formula'), element);
                  }
                });
                return returnValue;
              });
            }
            this.setDisabledTillFetched(false);
            return elements;
          }),
          map((elements: any) => {
            if (this.item?.attributes['translate']) {
              elements = elements.map(el => {
                  if (this.item.options.pathToSourceValue) {
                    const val = get(el, this.item.options.pathToSourceValue, []);
                    set(el, this.item.options.pathToSourceValue, this.getNormalizeValue(val));
                  }
                  else {
                    el = this.getNormalizeValue(el);
                  }
                  return el;

                }
              );
            }
            return elements;
          }),
          map((elements: any) => {
            if (!event.query) { return elements; }
            return this.localFilter(elements, event.query);
          })
        );
    }

    if ((this.localSearch && this.options$) || this.item['interpolateFromStore']) {
      this.options$ = this.options$.map(values => this.localFilter(values, event.query));
    }

    if (this.item.options.pathToSourceValue) {
      // pathToSourceValue gives us the ability to pick specific property of the returned items
        this.options$ = this.options$.pipe(
          takeUntil(this._destroyer.asObservable()),
            map((values: any[] ) => {
                values = values.map(val => get(val, this.item.options.pathToSourceValue, null));
                values = values.filter(v => !!v);
                return values;
            }));
    }

    this.options$ = this.options$.pipe(
        takeUntil(this._destroyer.asObservable()),
        map(values => this.filterSelected(values))
    );

    // filterKeysToRetain gives us the ability to retain the keys from option resource to store to backend mostly used in case of nested resource
    if(this.item.options.filterKeysToRetain && !isEmpty(this.item.options.filterKeysToRetain)) {
      this.options$ = this.options$.pipe(
        takeUntil(this._destroyer.asObservable()),
        map((values: any[] ) => {
          return values.map((value) => {
            return _.pick(value, this.item.options.filterKeysToRetain);
          });
        })
      )
      ;
    }
  }

  localFilter(values, query) {
    if (this.item.options.pathToValues) {
      values = values ?? [];
      values = get(values, this.item.options.pathToValues, values);
    }

    query = query.toLowerCase();
    if (this.item.options.useMultiSearch && query.length >0) {
      return this.multiSearchFilter(query, values, this.item.options.exactMultiSearch, get(this.item.options, 'searchField', undefined));
    } else {
      return this.simpleFilter(values, query);
    }
  }

  compareBy() {
    const self = this;

    return (a: any, b: any) => {
      const transformTemplate = self.transformToTemplate || { code: 'code', display: 'display'};
      return a && b && transformTemplate && a.hasOwnProperty(transformTemplate.code) && b.hasOwnProperty(transformTemplate.code)
        ? a[transformTemplate.code] === b[transformTemplate.code]
        : a === b;
    };
  }

  updateEditForm(itemKey) {
    if ((this.isClickable || this.isIndependent)) {
      return; // handled by the updateTouched function
    }

    this.itemTouched.emit({ name: itemKey, blured: true});
    this.updateForm(this.formInstance.controls[itemKey].value);
  }

  isEmptyItemValue(itemValue) {
    return !itemValue ||
           (
              itemValue.hasOwnProperty('code') && !itemValue.code &&
              itemValue.hasOwnProperty('display') && !itemValue.display
           );
  }

  calendarDateSelectedEvent(event){
    //Called when a single date was slected in a Range calendar - for some reason updateForm() is not called directly
    this.updateForm(event, false, false, true);
  }

  updateTouched(event, blured) {

    // in case that the item is marked to avoid emitting an empty value,
    // it is required to ignore current empty value
    // only if value is not changed to empty
    this.lastTouchedValue = this.getValue(this.item.name);

    if (this.isEmptyItemValue(this.lastTouchedValue) && this.noEmptyEmit) {
      // if it is on-focus event -OR- that the item's value on-focus is also empty, exit.
      // Else, value was cleared and therefore continue to emit the empty value
      if (! blured || this.isEmptyItemValue(this.lastTouchedValue)) {
        // workaround for GEN-168415: new attribute "noEmptyEmitName" works together with "noEmptyEmit"
        const _name = get(this.item.attributes, '[noEmptyEmitName]', false) ? this.item.name : null;
        this.itemTouched.emit({ name: _name, blured});

        return;
      }
    }

    // preserve the current item's value
    // this.lastTouchedValue = itemValue;
    blured = this.isClickable || blured;
    this.itemTouched.emit({ name: this.item.name, blured});

   if( this.item.attributes['[explictClickable]'] )
      {
        this.isClickable = true
      }

    if (this.isClickable) {
        this.updateForm(this.formInstance.controls[this.item.name].value);
    }
  }

  updateSubmitStatus(event) {
    this.submitUpdateEmitter.emit(event);
  }

  updateForm(value, isMultiple: boolean = false, add: boolean = true, ignorePreviousValue: boolean = false) {
    this.showMenu = null;
    let temp = this.getFormalizeStoreObject();

    if ((isMultiple || temp instanceof Array) && !ignorePreviousValue) { // if ignorePreviousValue take the new value (do check previous). In use when selecting a single date in a Range calendar
      let index;
      temp = temp || [];

      if (!add && (index = temp.findIndex(elem => {
        return value[this.transformToTemplate.code] === elem[this.transformToTemplate.code]
          && value[this.transformToTemplate.display] === elem[this.transformToTemplate.display];
      })) && index > -1) {
        temp.splice(index, 1);
      }
      else if (add) {
        temp = this.filterExistence(temp);
      }

      value = temp;
    }

    if (this.forceSelection === false && typeof (value) === 'string') {
      value = temp;
    }

    this.formInstance.controls[this.item.name].patchValue(value);
  }

  filterExistence(values) {
    const set = new Set();

    return values.filter(val => {
      const key = this.generateStringFromObject(val);

      return set.has(key) ? false : set.add(key);
    });
  }

  sanitizeMultiselectFormInput() {
    // workaround for GEN-202421
    if (this.item.type.toLowerCase() === 'multiselect') {
      this.multiselect.el.nativeElement.querySelector('.p-autocomplete-multiple-container li:last-of-type input').value = '';
    }
  }

  filterSelected(values) {
    const currentValue = this.getValue(this.item.name);

    if (currentValue && currentValue instanceof Array) {
      values = values.filter(value => {
        return currentValue instanceof Array
          ? !currentValue.find(val => this.generateStringFromObject(val) === this.generateStringFromObject(value))
          : this.generateStringFromObject(currentValue) === this.generateStringFromObject(value);
      });
    }

    return values || [];
  }

  getValue(itemKey = this.item.name) {
      if (this.item.isRootProperty) {
        if ((<string> this.item.valueType).toUpperCase() === VALUE_TYPES.ARRAY) {
          this._itemTempValue = this._itemTempValue || [];
          this._itemTempValue.length = 0;
          // getRawValue return new object each time, causing change detection loop if not working on the same reference
          const formValue = this.formInstance.getRawValue();
          this._itemTempValue.push.apply(this._itemTempValue, (<any> Object).values(formValue));
        } else {
          this._itemTempValue = this._itemTempValue || { };
          // getRawValue return new object each time, causing change detection loop if not working on the same reference
          this._itemTempValue = Object.assign(this._itemTempValue, this.formInstance.getRawValue());
        }

        return cloneDeep(this._itemTempValue);
      }
      else {
        let value = get(this.getControl(itemKey), 'value');
        if(this.item.type ==='textarea' &&
          value instanceof Object &&
          (value.hasOwnProperty('code') && value.code) &&
          (value.hasOwnProperty('display') && value.display)) {
            setTimeout(() => {
              this.formInstance.controls[this.item.name].patchValue(this.getFormalizeStoreObject());
            }, 10);
            return value.display;
          }
          else {
            return cloneDeep(value);
          }
      }
  }

  getControl(itemKey: string = this.item.name) {
    return this.formInstance.controls[itemKey]; // FormGroup.get() cause issue with field name which includes '.'
  }

  isFieldValid(itemKey: string = this.item.name) {
    return () => {
      return this.item.isRootProperty
        ? this.formInstance.valid
        : this.formInstance.controls[itemKey].valid;
    };
  }

  getInputCss(valueCss) {
    if (valueCss) {
      return valueCss.cssClass;
    }

    return '';
  }

  getToolTopVisibility(item) {
    if (item.tooltip) {
      if (item.tooltip.tooltipShow) {
        return item.tooltip.tooltipShow;
      }
    }

    return false;
  }

  getInvalidTooltipText(item, label) {
    let text =  `${label ? label : 'Value'}  is invalid`;
    const control = this.getControl(item.name);
    if (control) {
      const errors = control.errors && Object.keys(control.errors).filter(error => control.errors[error].message);
      if (errors && errors.length) {
        text += ' - ';
        errors.forEach(error => text += `\n${control.errors[error].message}`);
      }
    }
    return text;
  }

  /**
   * tooltip configuration display pattern will be use for data driven cards & interpolate of two different values
   * @param item
   */
  getTooltipText(item) {
    if (item && item.tooltip) {
      if (item.tooltip.tooltipText) {
        return item.tooltip.tooltipText;
      } else {
         return item.tooltip;
      }
    } else if (item && item.tooltipConfiguration && item.tooltipConfiguration.tooltipDisplayPattern) {
       return InterpolateService.interpolate(item.tooltipConfiguration.tooltipDisplayPattern, this.formInstance.getRawValue(), true);
    }

    return '';
  }

  getTooltipPosition(item) {
    return item.tooltip && item.tooltip.tooltipPosition
      ? item.tooltip.tooltipPosition
      : 'TOP';
  }

  ngAfterViewInit(): void {
    this.forceSelection = this.item.attributes && this.item.attributes['[forceSelection]'] !== undefined
      ? <boolean> this.item.attributes['[forceSelection]']
      : true;
    this.skipOnBlur = this.item.attributes && this.item.attributes['[skipOnBlur]'] !== undefined
      ? <boolean> this.item.attributes['[skipOnBlur]']
      : false;
    const element = this.element.nativeElement.querySelector('.dynamic_form_item_selector');

    if (element) {
      for (const attribute in this.item.attributes) {
        if (!FormItemComponent.inputsRegex.test(attribute)) {
          element.setAttribute(attribute, this.item.attributes[attribute]);
        }
      }
    }

    if (has(this.getControl(), 'valueChanges')) {
        this.tooltipText$ = this.getControl().valueChanges.pipe(map(value => this.getTooltipText(value)));
    }
  }

  getOptionByProperty(option, property) {
    return option && property && typeof option === 'object' && option[property]
      ? option[property]
      : option;
  }

  addNotSelectedOption(): boolean {
    return !this.item.options || this.item.options.allowNotSelect !== false;
  }

  private formalAttributes(item) {
    for (const attribute in item.attributes) {
      if (['[maxDate]', '[minDate]'].indexOf(attribute) > -1) {
        item.attributes[attribute] = this.setDate(<string> item.attributes[attribute]);
      }
    }

    this.forceSelection = item.attributes && item.attributes['[forceSelection]'] !== undefined
      ? <boolean> item.attributes['[forceSelection]']
      : true;

    this.multiple = item.attributes && item.attributes['[multiple]'] !== undefined
      ? <boolean> item.attributes['[multiple]']
      : true;
  }

  private getFieldsToPreserve(): string[] {
    if ((<string> this.item.fieldsToPreserve) === '*') {
        return null;
    }

    const fields = [];

    for (const key in this.transformToTemplate) {
      fields.push(this.transformToTemplate[key]);
    }

    const fieldsToPreserve = this.item.fieldsToPreserve || [];
    const keysToRetain = [];
    if (this.item.keysToRetain && Array.isArray(this.item.keysToRetain) && !isEmpty(this.item.keysToRetain)) {
      this.item.keysToRetain.forEach(field => keysToRetain.push(field.fromPath));
    }
    const fieldsArray = [...this.fieldsToDisplay, ...fieldsToPreserve as string[], ...keysToRetain];
    fieldsArray.forEach((field: string | Object) => {
      if (
        typeof field === 'object'
        && (!field.hasOwnProperty('source') || (field.hasOwnProperty('source') && field['source'] === 'item'))
      ) {
        fields.push(field['display']);
      }
      else if (typeof field !== 'object') {
        fields.push(field);
      }
    });

    return fields;
  }

  private getNormalizeValue(value) {
    if (!value) { return value; }

    let returnValue;

    if (typeof value !== 'object') {
      returnValue = value;
    }
    else if (value instanceof Array) {
      returnValue = value.map((val) => this.getNormalizeValue(val));
    }
    else {
      let fields = this.getFieldsToPreserve();

      if (fields === null) {
        returnValue = value;
      } else {
        returnValue = { };
        fields = fields.filter(item => !!item);
        for (const field of fields) {
          try {
            if (field == 'display' && this.item?.attributes['translate']) {
              set(returnValue, field, this.stringTranslate.transform(get(value, field)));
            }
            else {
              let val = get(value, field);
              if(!val){
                const key = this.getKeyByValue(this.transformToTemplate,field);
                val = get(value, key);
              }
              set(returnValue, field, val);
            }
          } catch (e) {
            continue;
          }
        }
      }
    }

    return returnValue;
  }

  private getKeyByValue(object, value) {
    return Object.keys(object).find(key => object[key] === value);
  }

  private generateStringFromObject(value) {
    return (value && typeof value === 'object')
      ? value[this.transformToTemplate.code] + value[this.transformToTemplate.display]
      : value;
  }

  private setDate(date: string): Date {
    switch (date) {
      case 'now':
        return this.dateTime;
      case null:
        return null;
      default:
        const parsedResult: Date = new Date(date);

        return !isNaN(parsedResult.valueOf()) ? parsedResult : null;
    }
  }

  onTextArea(event) {
    if (this.textAreaResizable) {
      if (event.target.clientHeight < event.target.scrollHeight) {
        event.target.style.height = (event.target.scrollHeight) + 'px';
      }
    }
  }

  linkClicked($event) {
    this.linkClick.emit($event);
  }

  truncate(showValue: any) {
    this.tooltipShow =  (has(this.item, 'tooltip') &&  !isEmpty(this.item.tooltip)) ? true : showValue ;
  }

  private setValueListener() {
    let controlCurrentValue, controlValue$;
    let fullFormCurrentValue;

    if (get(this.item, 'valueType')) {
      fullFormCurrentValue = (<string> this.item.valueType).toUpperCase() === VALUE_TYPES.ARRAY || !this.item.pathToField
        ? this.formInstance.getRawValue()
        : get(this.formInstance.getRawValue(), this.item.pathToField);
    }
    else {
      fullFormCurrentValue = this.item.pathToField
        ? get(this.formInstance.getRawValue(), this.item.pathToField)
        : this.formInstance.getRawValue();
    }

    if (this.item.isRootProperty) {
        const fullFormValue$ = this.formInstance.valueChanges.pipe(
            map(value => {
              if (get(this.item, 'valueType')) {
                if ((<string> this.item.valueType).toUpperCase() === VALUE_TYPES.ARRAY) {
                  return (<any> Object).values(this.formInstance.getRawValue());
                }
              }

              return this.item.pathToField
                ? get(this.formInstance.getRawValue(), this.item.pathToField)
                : this.formInstance.getRawValue();
            })
        );
        this.value$ = merge(Observable.of(fullFormCurrentValue), fullFormValue$);
    }
    else {
        controlCurrentValue = this.getControl().value;
        if (this._item.type == ITEMS_TYPES.TOGGLE_BUTTON) {
          this._alignment = controlCurrentValue;
        }
        controlValue$ = this.getControl().valueChanges;
        this.value$ = merge(Observable.of(controlCurrentValue), controlValue$);
    }

    this.fullFormData$ = merge(
      Observable.of(fullFormCurrentValue),
      this.formInstance.valueChanges.pipe(map(value => this.formInstance.getRawValue()))
    );
  }

  private needPrefetchOrNot(item: IdynamicFormItem): boolean {
    const resourceQuery = get(item, 'options.resource[0]');
    const control = this.getControl();


    if (control != null && control.disabled) {
      // Do not pre-fetch disabled fields - it caused an error when search query gets an empty parameter (in dropdown)
      return resourceQuery && !(InterpolateService.hasInterpolationString(resourceQuery));
    }

    if (resourceQuery=== 'null') {
      return false;
    }
    const prefetchNeeded = get(item.attributes, 'prefetchNeeded',true);
    if (!prefetchNeeded){
      return false;
    }
    // at the time of commit no dynamic form or code is using prefetchNeeded
    if (get(item.attributes, 'prefetchNeeded', false) === false && isEmpty(item.defaultValue) // Prefetch is needed in case item has a default value
        && this.getItemEnum(item.type) != ITEMS_TYPES.SELECT
        && this.getItemEnum(item.type) != ITEMS_TYPES.MULTISELECT
        && this.getItemEnum(item.type) != ITEMS_TYPES.MULTISELECT_WITH_MANUAL
        && this.getItemEnum(item.type) != ITEMS_TYPES.AUTOCOMPLETE) { // Items that do not fire 'search' (just show\hide a list) need to be prefetched
      return false;
    }

    return resourceQuery && !(InterpolateService.hasInterpolationString(resourceQuery));
  }

  getUpdateOnConfig(item: IdynamicFormItem, componentName: string): MOUSE_EVENT_TYPES {
    if (componentName) {
      if (FormItemComponent.DEFAULT_UNCLICKABLE_ON_UPDATE_COMPONENTS.includes(componentName.toUpperCase())) {
        return MOUSE_EVENT_TYPES.mousedown;
      }
    }
    else {
      return MOUSE_EVENT_TYPES.click;
    }
  }

  ngOnDestroy(): void {
    // this.contextMenuPayload = null;
    // this.editableParameter = null;
    // this.element = null;
    // this.lastQuery$ = null;
    // this.tooltipText$ = null;
    // this.options$ = null;
    // this.fullFormData$ = null;
    this._destroyer.next();
    this._destroyer.complete();
  }

  isNotEmptyLabel() {
    return !!this.item.label && this.item.label !== ' ';
  }

  isOptionDisabled(option): string | null {
    return (this.disabledOptions && this.disabledOptions.includes(option.code)) ? '' : null;
  }

  public radioClick(selectedType: string) {
    this.itemTouched.emit({ name: this.item.name, blured: true});
    this.formInstance.controls[this.item.name].setValue(selectedType);
  }

  getPrompt(prompt: string): string {
    return prompt.replace(/\\n/g, '\n', );
  }

  private simpleFilter(values, query) {
    return !!values ? values.filter(val => {
      let code, display, shortName, code1;
      //toString() added for cases then the externalCode is a number for some reason
      const extCode = val.externalCode ? val.externalCode.toString().toLowerCase() : '';

      if (typeof val === 'string') {
        code = display = val.toLowerCase();
      } else {
        if (this.item.options.pathToSourceValue) {
          val = get(val, this.item.options.pathToSourceValue);
        }
        if (!val) {
          return false;
        }
        if (!this.transformToTemplate) {
          code = display = (typeof val === 'string') && val.toLowerCase() || '';
        } else {
          code = val[this.transformToTemplate.code] == null ? '' : val[this.transformToTemplate.code].toString().toLowerCase();
          code1 = !!this.transformToTemplate.code1 ? (val[this.transformToTemplate.code1] == null ? '' : val[this.transformToTemplate.code1].toString().toLowerCase()) : '';

          if (val[this.transformToTemplate.display]) {
            display = val[this.transformToTemplate.display].toString().toLowerCase();
          } else {
            const tmpKey = this.splitPattern().find(itm => itm !== this.transformToTemplate.display);
            display = tmpKey && val[tmpKey] ? val[tmpKey].toString().toLowerCase() : '';
          }
        }
      }

      if (shortName) {
        return (display.includes(query) || shortName.includes(query));
      } else {
        const searchParameters: string[] = this.item.options.searchParameters || ['code', 'display', 'extCode', 'code1']
        return (((searchParameters.includes('code') && code.contains(query)) ||
          (searchParameters.includes('display') && display.contains(query)) ||
          (searchParameters.includes('extCode') && extCode?.contains(query)) ||
          (searchParameters.includes('code1') && code1?.contains(query))));
      }
    }) : [];
  }

  private multiSearchFilter(query, values, exactMultiSearch, searchField: string) {
    const queryChunks: string[] = StringUtils.getChunks(query);
    const chunksCount = queryChunks.length;

    return !!values ? values
      .filter(val => {
        let code, display, shortName, code1, weight = {val: 0}, result;
        const extCode = val.externalCode ? val.externalCode.toString().toLowerCase() : '';

        if (typeof val === 'string') {
          code = display = val.toLowerCase();
        } else {
          if (this.item.options.pathToSourceValue) {
            val = get(val, this.item.options.pathToSourceValue);
          }
          if (!val) {
            return false;
          }
          if (!this.transformToTemplate) {
            code = display = (typeof val === 'string') && val.toLowerCase() || '';
          } else {
            code = val[this.transformToTemplate.code] == null ? '' : val[this.transformToTemplate.code].toString().toLowerCase();
            code1 = !!this.transformToTemplate.code1 ? (val[this.transformToTemplate.code1] == null ? '' : val[this.transformToTemplate.code1].toString().toLowerCase()) : '';

            if (val[this.transformToTemplate.display]) {
              display = val[this.transformToTemplate.display].toString().toLowerCase();
            } else {
              const tmpKey = this.splitPattern().find(itm => itm !== this.transformToTemplate.display);
              display = tmpKey && val[tmpKey] ? val[tmpKey].toString().toLowerCase() : '';
            }
          }
        }

        weight.val = 0;

        if (shortName) {
          result = chunksCount > 0 ? (StringUtils.calcIsIncluded(display, queryChunks, weight, exactMultiSearch)
              || StringUtils.calcIsIncluded(shortName, queryChunks, weight, exactMultiSearch)) :

            (StringUtils.calcIsIncluded(display, [query], weight, exactMultiSearch)
              || StringUtils.calcIsIncluded(shortName, [query], weight, exactMultiSearch));
        } else {
          const searchFields = !!searchField ? [display] : [display, code, extCode, code1];
          result = chunksCount ?
            this.getSearchResult(queryChunks, weight, exactMultiSearch, searchFields) :
            this.getSearchResult([query], weight, exactMultiSearch, searchFields);
        }
        if (result && weight.val > 0) {
          set(val, 'weight', weight.val);
        }
        return result;
      })
      .sort((a, b) => b.weight - a.weight)
      .filter(val => !!exactMultiSearch ? val.weight >= chunksCount : true)
      .map(item => {
        unset(item, 'weight');
        return item;
      }) : [];
  }

  private getSearchResult(queryChunks: string[], weight: { val: number }, exactMultiSearch, searchFields: string[]) {
    return searchFields.some(searchField => StringUtils.calcIsIncluded(searchField, queryChunks, weight, exactMultiSearch));
  }
}
